import { Box, Button, Flex, HStack, Separator, Skeleton, Spinner } from "@chakra-ui/react"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { useState, useEffect } from "react"

// Utility function to check if a string looks like a UUID
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

interface CascadingAlertFiltersProps {
  filters: {
    customer?: string
    site?: string
    asset?: string
    alertStatus?: string
    alertSeverity?: string
    hasAlerts?: boolean
  }
  filterOptions?: {
    customers: Array<{ id: string; name: string; value: string }>
    sites: Array<{ id: string; name: string; value: string }>
    assets: Array<{ id: string; name: string; value: string }>
    alertStatuses: Array<{ id: string; name: string; value: string }>
  } | null
  onFiltersChange: (filters: CascadingAlertFiltersProps["filters"]) => void
  isRefetching?: boolean
}

function CascadingAlertFilters({ filters, filterOptions, onFiltersChange, isRefetching = false }: CascadingAlertFiltersProps) {
  const [localFilters, setLocalFilters] = useState(filters)

  // Update local state when props change
  useEffect(() => {
    setLocalFilters(filters)
  }, [filters])

  const handleFilterChange = (key: keyof CascadingAlertFiltersProps["filters"], value: string) => {
    const newValue = value === "" ? undefined : value

    // Validate that entity IDs are UUIDs (but allow alert status and severity to be non-UUID)
    if (newValue && (key === "customer" || key === "site" || key === "asset") && typeof newValue === "string") {
      if (!isValidUUID(newValue)) {
        console.warn(`Warning: ${key} filter value "${newValue}" does not appear to be a valid UUID. This may cause backend errors.`)
      }
    }
    const newFilters = {
      ...localFilters,
      [key]: newValue,
    }

    // Clear dependent filters when parent changes
    if (key === "customer") {
      newFilters.site = undefined
      newFilters.asset = undefined
    } else if (key === "site") {
      newFilters.asset = undefined
    }

    // Handle alert status changes
    if (key === "alertStatus") {

      switch (newValue) {
        case "no_alerts":
          newFilters.hasAlerts = false
          break
        case undefined:
          newFilters.hasAlerts = undefined
          break
        default:
          newFilters.hasAlerts = true
          break

      }
      newFilters.alertStatus = newValue as string
    }

    setLocalFilters(newFilters)
    onFiltersChange(newFilters)
  }

  const handleClearAll = () => {
    const clearedFilters = {
      customer: undefined,
      site: undefined,
      asset: undefined,
      alertStatus: undefined,
      hasAlerts: undefined,
    }
    setLocalFilters(clearedFilters)
    onFiltersChange(clearedFilters)
  }

  const hasFilters = Object.values(localFilters).some(value => value !== undefined)

  // Only show loading state on initial load, not on filter changes
  if (!filterOptions) {
    return (
      <Skeleton height="40px" width="100%" />
    )
  }

  const allowStatus = filterOptions.alertStatuses.length > 0
  const allowCustomer = filterOptions.customers.length > 0
  const allowSite = filterOptions.sites.length > 0 && localFilters.customer !== undefined
  const allowAsset = filterOptions.assets.length > 0 && (localFilters.site !== undefined || localFilters.customer !== undefined)

  return (
    <Box width="100%" py={3}>
      <HStack justify="space-between">
        <Flex alignItems="center" gap={4} wrap="wrap">

          {/* Status Filter */}
          <NativeSelectRoot size="sm" width="200px" disabled={!allowStatus}>
            <NativeSelectField
              value={localFilters.alertStatus || ""}
              onChange={(e) => handleFilterChange("alertStatus", e.target.value)}
              placeholder="All Statuses"
              items={([{ value: "no_alerts", name: "No Alerts" }, ...(filterOptions.alertStatuses || [])]).map((status) => ({
                value: status.value,
                label: status.name,
              }))}
            />
          </NativeSelectRoot>

          <Separator
            orientation="vertical" height="5"
          />

          {/* Customer Filter */}
          <NativeSelectRoot size="sm" width="200px" disabled={!allowCustomer}>
            <NativeSelectField
              value={localFilters.customer || ""}
              onChange={(e) => handleFilterChange("customer", e.target.value)}
              placeholder="All Customers"
              items={(filterOptions.customers || []).map((customer) => ({
                value: customer.value,
                label: customer.name,
              }))}
            />
          </NativeSelectRoot>

          {/* Site Filter */}
          <NativeSelectRoot size="sm" width="200px" disabled={!allowSite}>
            <NativeSelectField
              value={localFilters.site || ""}
              onChange={(e) => handleFilterChange("site", e.target.value)}
              placeholder="All Sites"
              items={(filterOptions.sites || []).map((site) => ({
                value: site.value,
                label: site.name,
              }))}
            />
          </NativeSelectRoot>

          {/* Asset Filter */}
          <NativeSelectRoot size="sm" width="200px" disabled={!allowAsset}>
            <NativeSelectField
              value={localFilters.asset || ""}
              onChange={(e) => handleFilterChange("asset", e.target.value)}
              placeholder="All Assets"
              items={(filterOptions.assets || []).map((asset) => ({
                value: asset.value,
                label: asset.name,
              }))}
            />
          </NativeSelectRoot>

          <Separator
            orientation="vertical" height="5"
          />
          <Button
            variant="hydrologiq_secondary"
            size="sm"
            disabled={!hasFilters || isRefetching}
            onClick={handleClearAll}
          >
            {isRefetching ? (
              <Flex alignItems="center" gap={2}>
                <Spinner size="xs" />
                Loading...
              </Flex>
            ) : (
              "Clear all"
            )}
          </Button>
        </Flex>
      </HStack>
    </Box>
  )
}

export default CascadingAlertFilters